import { useEffect, useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import {
  AtsVacanciesAssignHiringManagersDocument,
  AtsVacanciesTableDocument,
  AtsVacancyHiringManagersDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface Props {
  vacancyIds: string[]
  onClose: () => void
  onSuccess: () => void
}

export const AssignHiringManagersModal = ({ vacancyIds, onClose, onSuccess }: Props) => {
  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })
  const [fieldError, setFieldError] = useState<string | undefined>(undefined)

  const isSingleVacancy = vacancyIds.length === 1
  const { data: vacancyData } = useQuery(AtsVacancyHiringManagersDocument, {
    variables: { id: vacancyIds[0] ?? '0' },
    skip: !isSingleVacancy,
  })

  useEffect(() => {
    const hiringManagers = vacancyData?.ats.vacancy?.atsVacancyHiringManagers
    if (!hiringManagers?.length) return
    setEmployeeSelection({ type: 'manual', ids: hiringManagers.map((hm) => hm.id) })
  }, [vacancyData])

  const hasSelection =
    employeeSelection.type === 'all' ||
    (employeeSelection.type === 'manual' && employeeSelection.ids.length > 0)

  const [assignHiringManagers, { loading, client }] = useMutation(
    AtsVacanciesAssignHiringManagersDocument,
    {
      extractorFn: (result) => result.atsMutations.updateHiringManagersAtsVacancy,
    }
  )

  const handleSubmit = async () => {
    if (!hasSelection) {
      setFieldError(i18n.t('ats.vacancies.assign_hiring_managers_modal.validation_select_required'))
      return
    }

    if (employeeSelection.type !== 'manual') return

    setFieldError(undefined)
    const result = await assignHiringManagers({
      variables: {
        ids: vacancyIds,
        atsVacancyHiringManagerIds: employeeSelection.ids,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.vacancies.assign_hiring_managers_modal.error_message'),
      })
    })

    if (handled.success) {
      void client.refetchQueries({ include: [AtsVacanciesTableDocument] })
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.vacancies.assign_hiring_managers_modal.success_message'),
      })
      onSuccess()
    }
  }

  return (
    <F0Dialog
      title={i18n.t('ats.vacancies.assign_hiring_managers_modal.title')}
      isOpen
      onClose={onClose}
      primaryAction={{
        label: i18n.t('ats.vacancies.assign_hiring_managers_modal.save_button'),
        onClick: handleSubmit,
        disabled: !hasSelection,
        loading,
      }}
      secondaryAction={{
        label: i18n.t('ats.vacancies.assign_hiring_managers_modal.cancel'),
        onClick: onClose,
      }}
    >
      <EmployeeSelectorV2
        multiple
        value={employeeSelection.type === 'manual' ? employeeSelection.ids : []}
        onChange={(state) => {
          setEmployeeSelection(state)
          setFieldError(undefined)
        }}
        label={i18n.t('ats.vacancies.assign_hiring_managers_modal.select_label')}
        error={fieldError}
        mandatory
        options={{ disableSelectAll: true }}
      />
    </F0Dialog>
  )
}
